import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";
import { publicFeatureFlags } from "@/lib/feature-flags";

import { resolveMarkdownLink } from "./formatted-copy-markdown.utils";

export type FormattedCopyMarkdownVariant =
  | "intro"
  | "completion"
  | "body"
  | "conversation";

interface FormattedCopyMarkdownProps {
  content: string;
  variant?: FormattedCopyMarkdownVariant;
  className?: string;
}

const VARIANT_CLASSES: Record<
  FormattedCopyMarkdownVariant,
  {
    root: string;
    paragraph: string;
    paragraphSecondary?: string;
    heading1: string;
    heading2: string;
    heading3: string;
  }
> = {
  intro: {
    root: "space-y-3",
    paragraph: "m-0 text-3xl font-semibold leading-tight text-foreground",
    heading1: "form-heading m-0 text-4xl font-semibold leading-tight text-foreground",
    heading2: "form-heading m-0 text-3xl font-semibold leading-tight text-foreground",
    heading3: "form-heading m-0 text-2xl font-semibold leading-tight text-foreground",
  },
  completion: {
    root: "space-y-3",
    paragraph: "m-0 text-3xl font-semibold leading-tight text-foreground",
    paragraphSecondary: "m-0 text-lg font-medium leading-relaxed text-foreground",
    heading1: "form-heading m-0 text-4xl font-semibold leading-tight text-foreground",
    heading2: "form-heading m-0 text-3xl font-semibold leading-tight text-foreground",
    heading3: "form-heading m-0 text-2xl font-semibold leading-tight text-foreground",
  },
  body: {
    root: "space-y-2",
    paragraph: "m-0 text-base leading-relaxed text-muted-foreground",
    heading1: "form-heading m-0 text-2xl font-semibold leading-tight text-foreground",
    heading2: "form-heading m-0 text-xl font-semibold leading-tight text-foreground",
    heading3: "form-heading m-0 text-lg font-semibold leading-tight text-foreground",
  },
  conversation: {
    root: "space-y-2",
    paragraph: "m-0 text-muted-foreground leading-relaxed",
    heading1: "m-0 text-2xl font-semibold tracking-tight text-foreground",
    heading2: "m-0 text-xl font-semibold text-foreground",
    heading3: "m-0 text-lg font-semibold text-foreground",
  },
};

export function FormattedCopyMarkdown({
  content,
  variant = "body",
  className,
}: FormattedCopyMarkdownProps) {
  const styles = VARIANT_CLASSES[variant];
  let paragraphCount = 0;
  let hasRenderedHeading = false;

  const resolveParagraphClassName = () => {
    paragraphCount += 1;

    const shouldUsePrimaryParagraph =
      paragraphCount === 1 && !hasRenderedHeading;

    if (shouldUsePrimaryParagraph) {
      return styles.paragraph;
    }

    return styles.paragraphSecondary ?? styles.paragraph;
  };

  if (!publicFeatureFlags.formattedCopyEnabled) {
    return <p className={cn(styles.paragraph, className)}>{content}</p>;
  }

  return (
    <div className={cn(styles.root, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => {
            hasRenderedHeading = true;
            return <h1 className={styles.heading1}>{children}</h1>;
          },
          h2: ({ children }) => {
            hasRenderedHeading = true;
            return <h2 className={styles.heading2}>{children}</h2>;
          },
          h3: ({ children }) => {
            hasRenderedHeading = true;
            return <h3 className={styles.heading3}>{children}</h3>;
          },
          p: ({ children }) => (
            <p className={resolveParagraphClassName()}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="ml-5 list-disc space-y-1 text-sm text-muted-foreground">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children, ...props }) => {
            const resolved = resolveMarkdownLink(href);

            if (!resolved.href) {
              return <>{children}</>;
            }

            return (
              <a
                href={resolved.href}
                target={resolved.openInNewTab ? "_blank" : undefined}
                rel={resolved.openInNewTab ? "noreferrer noopener" : undefined}
                className="text-primary underline underline-offset-2 hover:opacity-90"
                {...props}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
